/* ------------------------------------------------------------------
  File: Header.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-08-15
------------------------------------------------------------------ */

// Purpose:
//   - Header renders the global website header.
//   - It uses navConfig as the single source of truth for primary navigation.
//   - It supports direct links and desktop mega menu navigation groups.
//   - It supports drill-down mobile navigation.
//   - Adds a filled header background after page scroll.
//   - Switches logo color based on scroll/menu state.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {NavLink, Link, useLocation} from "react-router-dom";
import { routesConfig } from "../../../../app/config/routesConfig";
import { navConfig } from "../../../../app/config/navConfig";

import {
  LuSearch,
  LuMenu,
  LuX,
  LuChevronDown,
  LuChevronRight,
  LuChevronLeft,
  LuArrowRight,
} from "react-icons/lu";

import { Button, IconButton } from "../../ui/Buttons";
import brandLogoColoredWhite from "../../../../assets/images/accend-holdings-logo-256-colored2.png";
import brandLogoColoredBlack from "../../../../assets/images/accend-holdings-logo-256-colored.png";

// import brandLogoColoredWhite from "../../../../assets/images/accend-holdings-logo-white-coloured.png";
// import brandLogoColoredBlack from "../../../../assets/images/accend-holdings-logo-black-coloured.png";

// import brandLogoColoredBlack from "../../../../assets/images/new-accend-holdings-logo-2026-09-brand-h512.png";
// import brandLogoColoredWhite from "../../../../assets/images/new-accend-holdings-logo-2026-09-white-h512.png";

// import brandLogoColoredBlack from "../../../../assets/images/new-accend-holdings-logo-2026-09-brand-h192.png";
// import brandLogoColoredWhite from "../../../../assets/images/new-accend-holdings-logo-2026-09-white-h192.png";
import MegaMenu from "../Navigation/MegaMenu/MegaMenu";
import "./Header.css";


const Header = () => {
  const location = useLocation();


  /* ========================================================
    STATE
  ======================================================== */

  // Mobile menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Stores the active mobile navigation item.
  // null = main mobile navigation.
  const [activeMobileMenu, setActiveMobileMenu] = useState(null);

  // Scroll state
  const [isScrolled, setIsScrolled] = useState(false);

  // Desktop mega menu state
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);

  // Desktop mega menu close timer
  const closeMegaMenuTimer = useRef(null);


  /* ========================================================
    NAVIGATION DATA
  ======================================================== */

  // Home is represented by the logo.
  // Contact is represented by the header CTA button.
  const headerNavItems = navConfig.filter(
    (item) =>
      item.to !== routesConfig.home &&
      item.to !== routesConfig.contact
  );

  const hasActiveHeaderState =
    isScrolled ||
    isMenuOpen ||
    activeMegaMenu;


  /* ========================================================
    DESKTOP MEGA MENU HANDLERS
  ======================================================== */

  const clearMegaMenuCloseTimer = useCallback(() => {
    if (closeMegaMenuTimer.current) {
      clearTimeout(closeMegaMenuTimer.current);
      closeMegaMenuTimer.current = null;
    }
  }, []);


  const closeMegaMenu = useCallback(() => {
    clearMegaMenuCloseTimer();
    setActiveMegaMenu(null);
  }, [clearMegaMenuCloseTimer]);


  const closeMegaMenuWithDelay = useCallback(() => {
    clearMegaMenuCloseTimer();

    closeMegaMenuTimer.current = setTimeout(() => {
      setActiveMegaMenu(null);
    }, 160);
  }, [clearMegaMenuCloseTimer]);


  const openMegaMenu = useCallback(
    (item) => {
      clearMegaMenuCloseTimer();

      if (item.megaMenu) {
        setActiveMegaMenu(item.megaMenu);
        return;
      }

      setActiveMegaMenu(null);
    },
    [clearMegaMenuCloseTimer]
  );


  /* ========================================================
    MOBILE MENU HANDLERS
  ======================================================== */

  const closeMobileMenu = useCallback(() => {
    setIsMenuOpen(false);
    setActiveMobileMenu(null);
  }, []);


  const openMobileSubmenu = (item) => {
    if (!item.megaMenu) return;

    setActiveMobileMenu(item);
  };


  const closeMobileSubmenu = () => {
    setActiveMobileMenu(null);
  };


  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);

    // Always reset mobile navigation to its root level.
    setActiveMobileMenu(null);

    // Prevent desktop mega menu remaining active.
    closeMegaMenu();
  };


  /* ========================================================
    HEADER INTERACTIONS
  ======================================================== */

  const handleHeaderClick = (event) => {
    const clickedMegaNavLink = event.target.closest(
      ".site-header__nav-link[aria-haspopup='true']"
    );

    const clickedMegaMenu = event.target.closest(
      ".acc-mega-menu"
    );

    if (!clickedMegaNavLink && !clickedMegaMenu) {
      closeMegaMenu();
    }
  };


  const handleNavKeyDown = (event, item) => {
    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      if (item.megaMenu) {
        event.preventDefault();

        setActiveMegaMenu((prev) =>
          prev === item.megaMenu
            ? null
            : item.megaMenu
        );
      }
    }

    if (event.key === "Escape") {
      closeMegaMenu();
    }
  };


  /* ========================================================
    SCROLL EFFECT
  ======================================================== */

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();

    window.addEventListener(
      "scroll",
      handleScroll,
      { passive: true }
    );

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, []);


  /* ========================================================
    MOBILE MENU EFFECTS
  ======================================================== */

  // Close mobile navigation whenever the route changes.
  useEffect(() => {
    closeMobileMenu();
  }, [
    location.pathname,
    closeMobileMenu,
  ]);


  // Prevent document scrolling while mobile navigation is open.
  useEffect(() => {
    if (!isMenuOpen) return;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [isMenuOpen]);


  /* ========================================================
    GLOBAL ESCAPE KEY
  ======================================================== */

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key !== "Escape") return;

      // If inside a mobile submenu,
      // Escape first returns to the root menu.
      if (isMenuOpen && activeMobileMenu) {
        setActiveMobileMenu(null);
        return;
      }

      closeMegaMenu();
      closeMobileMenu();
    };

    window.addEventListener(
      "keydown",
      handleEscapeKey
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleEscapeKey
      );
    };
  }, [
    isMenuOpen,
    activeMobileMenu,
    closeMegaMenu,
    closeMobileMenu,
  ]);


  /* ========================================================
    CLEANUP
  ======================================================== */

  useEffect(() => {
    return () => {
      clearMegaMenuCloseTimer();
    };
  }, [clearMegaMenuCloseTimer]);


  /* ========================================================
    RENDER
  ======================================================== */

  return (
    <header
      className={`
        site-header
        ${
          hasActiveHeaderState
            ? "site-header--scrolled"
            : ""
        }
        ${
          activeMegaMenu
            ? "site-header--mega-open"
            : ""
        }
      `}
      onMouseEnter={clearMegaMenuCloseTimer}
      onMouseLeave={closeMegaMenuWithDelay}
      onClick={handleHeaderClick}
    >

      {/* =====================================================
        HEADER BAR
      ====================================================== */}

      <div className="site-header__inner l-container">

        {/* Brand */}
        <Link
          to={routesConfig.home}
          className="site-header__brand"
          onFocus={closeMegaMenu}
          onClick={() => {
            closeMegaMenu();
            closeMobileMenu();
          }}
        >
          <img
            src={
              hasActiveHeaderState
                ? brandLogoColoredBlack
                : brandLogoColoredWhite
            }
            alt="Accend Holdings Logo"
            className="site-header__brand-logo"
          />
        </Link>


        {/* =====================================================
          DESKTOP NAVIGATION
        ====================================================== */}

        <nav
          className="site-header__nav site-header__nav--desktop"
          aria-label="Primary navigation"
        >
          <ul className="site-header__nav-list">

            {headerNavItems.map((item) => {
              const hasMegaMenu =
                Boolean(item.megaMenu);

              const isActiveMegaItem =
                activeMegaMenu === item.megaMenu;

              return (
                <li
                  key={item.to || item.label}
                  className="site-header__nav-item"
                  onMouseEnter={() =>
                    openMegaMenu(item)
                  }
                  onFocus={() =>
                    openMegaMenu(item)
                  }
                >

                  {hasMegaMenu ? (
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `
                          site-header__nav-link
                          ${
                            isActive ||
                            isActiveMegaItem
                              ? "site-header__nav-link--active"
                              : ""
                          }
                        `
                      }
                      aria-haspopup="true"
                      aria-expanded={
                        isActiveMegaItem
                      }
                      onKeyDown={(event) =>
                        handleNavKeyDown(
                          event,
                          item
                        )
                      }
                    >
                      <span>
                        {item.label}
                      </span>

                      <LuChevronDown
                        className="site-header__nav-chevron"
                        aria-hidden="true"
                      />
                    </NavLink>
                  ) : (
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `
                          site-header__nav-link
                          ${
                            isActive
                              ? "site-header__nav-link--active"
                              : ""
                          }
                        `
                      }
                      onFocus={closeMegaMenu}
                      onMouseEnter={closeMegaMenu}
                    >
                      {item.label}
                    </NavLink>
                  )}

                </li>
              );
            })}

          </ul>
        </nav>


        {/* =====================================================
          MOBILE MENU TOGGLE
        ====================================================== */}

        <IconButton
          icon={
            isMenuOpen
              ? <LuX />
              : <LuMenu />
          }
          onClick={toggleMenu}
          aria-label={
            isMenuOpen
              ? "Close menu"
              : "Open menu"
          }
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation"
          className="site-header__button--menu-toggle"
        />


        {/* =====================================================
          DESKTOP HEADER ACTIONS
        ====================================================== */}

        <div
          className="site-header__actions l-cluster l-cluster--spaced"
          onFocus={closeMegaMenu}
          onMouseEnter={closeMegaMenu}
        >
          <IconButton
            icon={<LuSearch />}
            size="sm"
            className="site-header__button--search"
          />

          <Button
            as={Link}
            to={routesConfig.contact}
            size="sm"
            variant="outline"
            className="site-header__button--contact"
            onClick={closeMegaMenu}
          >
            Contact us
          </Button>
        </div>

      </div>


      {/* =====================================================
        MOBILE NAVIGATION
      ====================================================== */}

      <div
        id="mobile-navigation"
        className={`
          site-header__mobile-nav
          ${
            isMenuOpen
              ? "site-header__mobile-nav--open"
              : ""
          }
        `}
        aria-hidden={!isMenuOpen}
      >
        <div className="site-header__mobile-nav-inner">


          {/* =================================================
            MOBILE ROOT MENU
          ================================================== */}

          {!activeMobileMenu && (
            <nav
              className="site-header__mobile-navigation"
              aria-label="Mobile navigation"
            >

              <ul className="site-header__mobile-list">

                {headerNavItems.map((item) => {
                  const hasMegaMenu =
                    Boolean(item.megaMenu);

                  const itemKey =
                    item.to || item.label;

                  return (
                    <li
                      key={itemKey}
                      className="site-header__mobile-item"
                    >

                      {hasMegaMenu ? (
                        <Button
                          variant="outlined"
                          fullWidth="true"
                          icon={<LuChevronRight aria-hidden="true" />}
                          onClick={() => openMobileSubmenu(item)}
                          className="site-header__mobile-main-link"
                        >
                          {item.label}
                        </Button>
                      ) : (
                        // TODO: REVIEW THIS CODE
                        <NavLink
                          to={item.to}
                          className={({
                            isActive,
                          }) =>
                            `
                              site-header__mobile-main-link
                              ${
                                isActive
                                  ? "site-header__mobile-main-link--active"
                                  : ""
                              }
                            `
                          }
                          onClick={
                            closeMobileMenu
                          }
                        >
                          <span>
                            {item.label}
                          </span>

                          <LuChevronRight
                            aria-hidden="true"
                          />
                        </NavLink>
                      )}

                    </li>
                  );
                })}

              </ul>

            </nav>
          )}


          {/* =================================================
            MOBILE DRILL-DOWN SUBMENU
          ================================================== */}

          {activeMobileMenu && (
            <div className="site-header__mobile-panel">

              {/* ---------------------------------------------
                Back button
              ---------------------------------------------- */}

              <Button
                as={Link}
                size="sm"
                variant="link"
                className="site-header__mobile-back"
                onClick={closeMobileSubmenu}
                iconPosition="left"
                icon={<LuChevronLeft aria-hidden="true"/>}
              >
                Go back
              </Button>


              {/* ---------------------------------------------
                Submenu header
              ---------------------------------------------- */}

              <div className="site-header__mobile-panel-header l-stack l-stack--sm">

                <h2 className="site-header__mobile-panel-title">
                  {activeMobileMenu.label}
                </h2>

                {activeMobileMenu.megaMenu
                  ?.description && (
                  <p className="site-header__mobile-panel-description">
                    {
                      activeMobileMenu
                        .megaMenu
                        .description
                    }
                  </p>
                )}

              </div>


              {/* ---------------------------------------------
                Navigation groups
              ---------------------------------------------- */}

              <div className="site-header__mobile-groups">

                {activeMobileMenu.megaMenu
                  ?.columns
                  ?.map((column) => (
                    <section
                      key={column.title}
                      className="site-header__mobile-group l-stack l-stack--md"
                    >

                      {column.title && (
                        <h6 className="site-header__mobile-group-title">
                          {column.title}
                        </h6>
                      )}


                      <ul className="site-header__mobile-group-list l-stack l-stack--sm">

                        {column.items?.map(
                          (subItem) => {
                            const hasChildren =
                              subItem.children
                                ?.length > 0;

                            return (
                              <li
                                key={
                                  subItem.label
                                }
                                className="site-header__mobile-group-item"
                              >

                                {/* -------------------------
                                  Normal submenu item
                                -------------------------- */}

                                {!hasChildren && (

                                  <Button
                                    as={Link}
                                    variant="link"
                                    onClick={closeMobileMenu}
                                    icon={<LuChevronRight aria-hidden="true" />}
                                    className="site-header__mobile-sub-link"
                                  >
                                    {subItem.label}
                                  </Button>
                                )}


                                {/* -------------------------
                                  Nested children
                                -------------------------- */}

                                {hasChildren && (
                                  <div className="site-header__mobile-nested-group l-stack l-stack--md">

                                    <h6 className="site-header__mobile-nested-title">
                                      {
                                        subItem.label
                                      }
                                    </h6>

                                    <ul className="site-header__mobile-nested-list l-stack l-stack--sm">

                                      {subItem.children.map(
                                        (
                                          child
                                        ) => (
                                          <li
                                            key={
                                              child.label
                                            }
                                          >
                                            <Button
                                              as={Link}
                                              variant="link"
                                              to={child.to}
                                              icon={<LuChevronRight aria-hidden="true" />}
                                              className="site-header__mobile-nested-link"
                                              onClick={closeMobileMenu}
                                            >
                                              {child.label}
                                            </Button>
                                          </li>
                                        )
                                      )}

                                    </ul>

                                  </div>
                                )}

                              </li>
                            );
                          }
                        )}

                      </ul>


                      {/* -------------------------------------
                        Column CTA
                      -------------------------------------- */}

                      {column.cta?.label &&
                        column.cta?.to && (
                          <Button
                            as={Link}
                            size="sm"
                            fullWidth="true"
                            to={column.cta.to}
                            variant="link"
                            icon={<LuArrowRight aria-hidden="true" />}
                            className="site-header__mobile-group-cta"
                            onClick={closeMobileMenu}
                          >
                            {column.cta.label}
                          </Button>

                        )}

                    </section>
                  ))}

              </div>


              {/* ---------------------------------------------
                Parent page CTA
              ---------------------------------------------- */}

              {activeMobileMenu.to && (
                <Button
                  as={Link}
                  size="sm"
                  fullWidth="true"
                  to={activeMobileMenu.to}
                  icon={<LuArrowRight aria-hidden="true" />}
                  className="site-header__mobile-parent-link"
                  onClick={closeMobileMenu}
                >
                  View{" "}
                    {activeMobileMenu.label}
                </Button>
              )}

            </div>
          )}


          {/* =================================================
            MOBILE ROOT ACTIONS
          ================================================== */}

          {!activeMobileMenu && (
            <div className="site-header__mobile-actions">

              <Button
                as={Link}
                size="sm"
                variant="outlined"
                to={routesConfig.search}
                fullWidth="true"
                iconPosition="left"
                icon={<LuSearch aria-hidden="true"/>}
                className="site-header__mobile-search"
                onClick={closeMobileMenu}
              >
                Search site
              </Button>

              <Button
                as={Link}
                size="sm"
                to={routesConfig.contact}
                fullWidth="true"
                icon={<LuArrowRight />}
                className="site-header__mobile-contact"
                onClick={closeMobileMenu}
              >
                Contact us
              </Button>

            </div>
          )}

        </div>
      </div>


      {/* =====================================================
        DESKTOP MEGA MENU
      ====================================================== */}

      {activeMegaMenu && (
        <div
          className="site-header__mega-menu-wrapper"
          onMouseEnter={
            clearMegaMenuCloseTimer
          }
          onMouseLeave={
            closeMegaMenuWithDelay
          }
        >
          <MegaMenu
            menu={activeMegaMenu}
          />
        </div>
      )}

    </header>
  );
};


export default Header;